/**
 * StripePaymentForm - Formulaire de paiement Stripe avec Elements
 * Design System v2 - Style Landing (page publique)
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Loader2, AlertCircle, Lock } from 'lucide-react';
import { logger } from '@/utils/logger';

interface StripePaymentFormProps {
  clientSecret: string;
  publishableKey: string;
  amount: number;
  currency: string;
  onSuccess: () => void;
  onError: (error: string) => void;
}

// Composant interne pour le formulaire Stripe
const CheckoutForm: React.FC<{
  amount: number;
  currency: string;
  onSuccess: () => void;
  onError: (error: string) => void;
}> = ({ amount, currency, onSuccess, onError }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${window.location.pathname}?payment=success`,
        },
        redirect: 'if_required',
      });

      if (error) {
        logger.error('Stripe payment error:', error);
        setMessage(error.message || t('publicPayment.stripe.genericError'));
        onError(error.message || t('publicPayment.stripe.paymentError'));
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        logger.info('Payment succeeded:', paymentIntent.id);
        onSuccess();
      } else if (paymentIntent && paymentIntent.status === 'requires_action') {
        // 3D Secure ou autre action requise - géré automatiquement par Stripe
        logger.info('Payment requires action');
      } else {
        // Redirection vers return_url
        logger.info('Redirecting to return_url');
      }
    } catch (err) {
      logger.error('Payment submission error:', err);
      setMessage(t('publicPayment.stripe.unexpectedError'));
      onError(t('publicPayment.stripe.unexpectedError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Stripe Payment Element */}
      <div className="bg-white rounded-2xl p-4">
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {/* Message d'erreur */}
      {message && (
        <div className="flex items-center gap-2 p-4 bg-red-500/20 rounded-xl border border-red-400/30">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-red-200 text-sm">{message}</p>
        </div>
      )}

      {/* Bouton de paiement */}
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full py-4 bg-gradient-to-r from-lime-400 to-lime-500 text-teal-800 font-bold rounded-xl
          hover:shadow-lg hover:shadow-lime-400/25 hover:-translate-y-0.5
          disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0
          transition-all duration-300 flex items-center justify-center gap-3"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('publicPayment.stripe.processing')}
          </>
        ) : (
          <>
            <Lock className="w-5 h-5" />
            {t('publicPayment.stripe.pay', { amount: formatAmount(amount, currency) })}
          </>
        )}
      </button>

      {/* Sécurité */}
      <div className="flex items-center justify-center gap-2 text-white/40 text-xs">
        <Lock className="w-3 h-3" />
        <span>{t('publicPayment.stripe.securedBy')}</span>
      </div>
    </form>
  );
};

// Composant principal avec Provider Stripe
export const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  clientSecret,
  publishableKey,
  amount,
  currency,
  onSuccess,
  onError,
}) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    if (publishableKey) {
      setStripePromise(loadStripe(publishableKey));
    }
  }, [publishableKey]);

  if (!stripePromise || !clientSecret) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-8 h-8 text-lime-400 animate-spin" />
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#84cc16',
            colorBackground: '#ffffff',
            colorText: '#1f2937',
            colorDanger: '#ef4444',
            fontFamily: 'system-ui, sans-serif',
            borderRadius: '12px',
            spacingUnit: '4px',
          },
        },
        locale: 'fr',
      }}
    >
      <CheckoutForm
        amount={amount}
        currency={currency}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
};

export default StripePaymentForm;
